import shortId from "shortid";
import { toast } from "react-toastify";
import { captureException } from "@sentry/react";

import SearchAction from "../../helpers/api/search-action";
import { isMainGroupVisible } from "../../helpers/main-group-visibility";

import dummyAssets from "../../dummy-data/assets";

import { setOffset, setSubscription } from "./search-filter";
import { LS_KEY_SEARCH_FILTER_SUBSCRIPTION } from "../../constants/localStorage";
import { GA } from "../../hooks/useGoogleAnalytics";
import { isEmpty, mapKeys, omit, snakeCase } from "lodash";
import { setExpandClick, setIsBucketLoading } from "./aggregations";

//Action Types
export const FETCHING_ASSETS = "FETCHING_ASSETS";
export const SET_LATEST_FETCH_ID = "SET_LATEST_FETCH_ID";
export const ASSETS_RECEIVED = "ASSETS_RECEIVED";
export const SET_HUMAN_RESULT = "SET_HUMAN_RESULT";
export const SET_HUMAN_RESULTS_SIGNATURE = "SET_HUMAN_RESULTS_SIGNATURE";
export const SET_AGGREGATED_FILTERS = "SET_AGGREGATED_FILTERS";
export const SET_SEARCH_INSTANCE = "SET_SEARCH_INSTANCE";
export const SET_SEARCH_VIEW_CONFIG = "SET_SEARCH_VIEW_CONFIG";
export const SET_SEARCH_LOADING = "SET_SEARCH_LOADING";
export const SET_OTHER_MAIN_GROUPS_WITH_RESULTS = "ASSET_SET_OTHER_MAIN_GROUPS_WITH_RESULTS";

// Tracks the AbortController for the currently active primary search so that
// a new search can cancel any superseded in-flight request.
let currentSearchController = null;

const makeCurrentSearch = searchFilter => {
  return new SearchAction()
    .searchString(searchFilter.searchString)
    .mainGroup(searchFilter.mainGroup)
    .sortOrder(searchFilter.sortOrder)
    .offset(searchFilter.offset)
    .searchAfter(searchFilter.searchAfter)
    .limit(searchFilter.limit)
    .photographer(searchFilter.photographer)
    .distributorName(searchFilter.distributorName)
    .credit(searchFilter.credit)
    .keywords(searchFilter.keywords)
    .subjectTerms(searchFilter.subjectTerms)
    .editorialTopics(searchFilter.editorialTopics)
    .subscription(searchFilter.subscription)
    .timeLimit(searchFilter.timeLimit)
    .createdMin(searchFilter.createdMin)
    .createdMax(searchFilter.createdMax)
    .events(searchFilter.events)
    .persons(searchFilter.persons)
    .properNames(searchFilter.properNames)
    .places(searchFilter.places)
    .city(searchFilter.city)
    .country(searchFilter.country)
    .pictureOrientation(searchFilter.pictureOrientation)
    .colors(searchFilter.colors)
    .stockColor(searchFilter.stockColor)
    .blackAndWhite(searchFilter.blackAndWhite)
    .clearEdge(searchFilter.clearEdge)
    .creativeTopics(searchFilter.creativeTopics)
    .creativePeople(searchFilter.creativePeople)
    .stockImageType(searchFilter.stockImageType)
    .videoEdit(searchFilter.videoEdit)
    .videoTopics(searchFilter.videoTopics)
    .vgTopics(searchFilter.vgTopics)
    .assignmentNumbers(searchFilter.assignmentNumbers)
    .refPtrs(searchFilter.refPtrs);
};

export const fetchAggregations = () => {
  return async (dispatch, getState) => {
    const aggregations = getState().aggregations;
    const searchFilter = getState().searchFilter;
    // IM-246: Fixed multiple supplier selection issue
    // Earlier we could not select multiple suppliers, so we had to remove the distributorName from the searchFilter
    const searchFilterExcludingDistributors = {
      ...searchFilter,
      distributorName: []
    };
    const search = makeCurrentSearch(searchFilterExcludingDistributors);
    const { loadedFilterGroup, loadedAggregationGroup } = aggregations;

    if (isEmpty(loadedAggregationGroup) && isEmpty(loadedFilterGroup)) {
      dispatch(setAggregatedFilters([]));
      return;
    }

    dispatch(setIsBucketLoading(true));

    search
      .giveAggregations(true)
      .returnAllAggregation(false)
      .aggregationGroup(loadedAggregationGroup)
      .filterGroup(loadedFilterGroup)
      .useUrl(null)
      .limit(0)
      .searchAfter([])
      .fetch()
      .then(res => {
        dispatch(setAggregatedFilters(res.aggregations));
        dispatch(setIsBucketLoading(false));
        dispatch(setExpandClick(undefined));
      })
      .catch(error => {
        dispatch(setIsBucketLoading(false));
        dispatch(setExpandClick(undefined));
        toast.error("En feil oppstod under henting av søkefilter – prøv igjen litt senere");
        captureException(error);
      });
  };
};

//Action Creators
export const fetchAssets = (force = false, append = false, prepend = false) => {
  return async (dispatch, getState) => {
    const assetSearch = getState().assetSearch;
    const searchFilter = getState().searchFilter;
    const user = getState().user;

    const currentRequestRequiresAuth = Boolean(
      !user.authenticated &&
        searchFilter.possibleMainGroups.find(
          mainGroup =>
            mainGroup.mainGroup === searchFilter.mainGroup && mainGroup.requiresAuthentication
        )
    );

    let thisSearchUrl = "";
    const search = makeCurrentSearch(searchFilter);
    search.useUrl(url => {
      thisSearchUrl = url;
    });

    if (assetSearch.lastSearchUrl === thisSearchUrl && !force) {
      // Prevent unnecessary additional search
      return;
    }

    dispatch(setSearchLoading(true));
    dispatch(fetchingAssets(thisSearchUrl, append, prepend));

    if (currentRequestRequiresAuth) {
      search.limit(0);
      dispatch(assetsReceived({ images: dummyAssets, numResults: 1 }, append, prepend));
    }

    // For primary (non-scroll) searches, cancel any superseded in-flight request
    // so the backend/search engine is not doing work that will be discarded.
    if (!append && !prepend) {
      if (currentSearchController) {
        currentSearchController.abort();
      }
      currentSearchController = new AbortController();
    }
    const currentSignal = !append && !prepend ? currentSearchController.signal : undefined;

    const fetchId = shortId();
    if (!append && !prepend) {
      dispatch(setLastFetchId(fetchId));

      const viewConfig = assetSearch.searchViewConfig;
      if (
        viewConfig.defaultSubscriptionSearch &&
        !window.localStorage.getItem(LS_KEY_SEARCH_FILTER_SUBSCRIPTION)
      ) {
        // set default subscription if nothing is stored in localStorage
        search.subscription("subscription");

        // update lastSearchUrl to prevent duplicate searches
        const currestSearchUrl = thisSearchUrl;
        search.useUrl(url => {
          thisSearchUrl = url;
        });

        if (currestSearchUrl !== thisSearchUrl) {
          dispatch(fetchingAssets(thisSearchUrl, append, prepend));
        }

        // commit the new subscription setting
        dispatch(setSubscription("subscription"));
      }
    }

    if (prepend || force) {
      // we don't want a offset because we are prepending the results
      search.offset(0).searchAfter([]);
    }

    dispatch(
      setSearchInstance(Object.assign(Object.create(Object.getPrototypeOf(search)), search))
    );

    if (searchFilter.searchAfter?.length <= 0 && searchFilter.offset === 0 && !append && !prepend) {
      GA.event("asset_search", {
        ...mapKeys(
          omit(searchFilter, [
            "limit",
            "searchAfter",
            "offset",
            "menuOpen",
            "possibleMainGroups",
            "defaultFilters",
            "tempSearchString"
          ]),
          (value, key) => `search_filter_${snakeCase(key)}`
        )
      });
    }

    search
      .fetch({ returnFullResponse: true, ky: { fetchId, signal: currentSignal } })
      .then(async res => {
        let data = await res.json();

        dispatch(setLastFetchId(fetchId));

        if (!append && !prepend && getState().assetSearch.fetchId !== res.options.fetchId) {
          return;
        }

        // Sometime while scrolling data from microstock mixup with other groups.
        // To prevent that we need to check if current mainGroup is same as the
        // previously requested maingroup.
        if (
          getState().searchFilter.mainGroup !== searchFilter.mainGroup ||
          getState().searchFilter.searchString !== searchFilter.searchString
        ) {
          return;
        }

        if (prepend) {
          data.images = data.images.filter(newAsset => {
            return !assetSearch.assets.some(asset => asset.refPtr === newAsset.refPtr);
          });

          // If the result contains any search after value we don't need to calculate offset
          const searchAfter = data.searchAfter;
          if (!searchAfter || searchAfter.length <= 0) {
            //set offset
            if (data.images.length) {
              const newOffset = searchFilter.offset + data.images.length;
              dispatch(setOffset(newOffset));
            }
          }
        }

        if (!currentRequestRequiresAuth) {
          dispatch(assetsReceived(data, append, prepend));
          if ((data?.numResults ?? 0) > 0) {
            dispatch(setSearchLoading(false));
          }
        }

        if (!append && !prepend) {
          dispatch(setHumanResult(searchFilter.mainGroup, data.humanNumResults));

          if (!currentRequestRequiresAuth || !!user.authenticated) {
            dispatch(fetchAggregations());
          }

          const currentMainGroupObject = searchFilter.possibleMainGroups.find(
            possibleMainGroup => possibleMainGroup.mainGroup === searchFilter.mainGroup
          );

          // Signature covers all search inputs that affect sibling counts,
          // excluding the active group, pagination, and UI-only state.
          // When only the tab changes the signature is identical and we can
          // skip re-querying counts that are still valid in humanResults.
          const humanResultsSignature = JSON.stringify(
            omit(searchFilter, [
              "mainGroup",
              "limit",
              "searchAfter",
              "offset",
              "menuOpen",
              "possibleMainGroups",
              "defaultFilters",
              "tempSearchString"
            ])
          );
          // numResults === 0 still runs the loop for alsoCheck cross-media fallback
          const siblingCountsStillValid =
            assetSearch.humanResultsSignature === humanResultsSignature && data.numResults !== 0;

          if (!siblingCountsStillValid) {
            // Build the list of visible sibling groups to query for counts.
            // Uses isMainGroupVisible so this list always matches the tab list.
            const otherMainGroupsToGetHumanResultsFrom = searchFilter.possibleMainGroups.filter(
              possibleMainGroup =>
                possibleMainGroup.mainGroup !== searchFilter.mainGroup &&
                isMainGroupVisible(
                  possibleMainGroup,
                  currentMainGroupObject.type,
                  assetSearch.searchViewConfig
                ) &&
                !possibleMainGroup.external
            );

            // add mainGroups with `alsoCheck` if the search result is empty on the active `mainGroup`
            if (data.numResults === 0) {
              const alsoCheckMainGroupObjects = searchFilter.possibleMainGroups.filter(
                possibleMainGroup =>
                  possibleMainGroup.mainGroup !== searchFilter.mainGroup &&
                  isMainGroupVisible(
                    possibleMainGroup,
                    possibleMainGroup.type,
                    assetSearch.searchViewConfig
                  ) &&
                  currentMainGroupObject.type !== possibleMainGroup.type &&
                  possibleMainGroup.alsoCheck
              );

              for (const alsoCheckMainGroupObject of alsoCheckMainGroupObjects) {
                otherMainGroupsToGetHumanResultsFrom.push(alsoCheckMainGroupObject);
              }

              dispatch(setOtherMainGroupsWithResults([]));
            }

            // Each count query gets its own SearchAction instance to avoid
            // mutating the shared builder, and inherits the abort signal so
            // superseded searches cancel their sibling count queries too.
            const fetchingResultsFromOtherBasesPromises = [];
            for (const mainGroupObject of otherMainGroupsToGetHumanResultsFrom) {
              fetchingResultsFromOtherBasesPromises.push(
                (async () => {
                  const countSearch = makeCurrentSearch(searchFilter);
                  const response = await countSearch
                    .mainGroup(mainGroupObject.mainGroup)
                    .limit(0)
                    .searchAfter([])
                    .giveAggregations(false)
                    .fetch({ ky: { signal: currentSignal } });

                  dispatch(setHumanResult(mainGroupObject.mainGroup, response.humanNumResults));
                })()
              );
            }

            try {
              await Promise.all(fetchingResultsFromOtherBasesPromises);

              if (data.numResults === 0) {
                const currentHumanResults = getState().assetSearch.humanResults;
                const otherMainGroupsWithResults = otherMainGroupsToGetHumanResultsFrom.filter(
                  possibleMainGroup => {
                    return (
                      currentHumanResults[possibleMainGroup.mainGroup] &&
                      currentHumanResults[possibleMainGroup.mainGroup] !== "0" &&
                      possibleMainGroup.alsoCheck
                    );
                  }
                );

                dispatch(setOtherMainGroupsWithResults(otherMainGroupsWithResults));
              }
            } catch (e) {}

            dispatch(setHumanResultsSignature(humanResultsSignature));
          }

          dispatch(setSearchLoading(false));
        }
      })
      .catch(error => {
        // AbortError is expected when a newer search starts — not a real error
        if (error.name === "AbortError") {
          return;
        }

        dispatch(assetsReceived({ images: [] }));
        dispatch(setSearchLoading(false));
        toast.error("En feil oppstod under søket – prøv igjen litt senere");
        captureException(error);
      });
  };
};

const fetchingAssets = (url, append, prepend) => ({
  type: FETCHING_ASSETS,
  url,
  append,
  prepend
});

const setLastFetchId = value => ({
  type: SET_LATEST_FETCH_ID,
  value
});

const setOtherMainGroupsWithResults = value => ({
  type: SET_OTHER_MAIN_GROUPS_WITH_RESULTS,
  value
});

const assetsReceived = (value, append, prepend) => ({
  type: ASSETS_RECEIVED,
  value,
  append,
  prepend
});

const setHumanResult = (mainGroup, value) => ({
  type: SET_HUMAN_RESULT,
  mainGroup,
  value
});

const setHumanResultsSignature = value => ({
  type: SET_HUMAN_RESULTS_SIGNATURE,
  value
});

const setAggregatedFilters = value => ({
  type: SET_AGGREGATED_FILTERS,
  value
});

const setSearchInstance = value => ({
  type: SET_SEARCH_INSTANCE,
  value
});

export const setSearchViewConfig = value => ({
  type: SET_SEARCH_VIEW_CONFIG,
  value
});

export const setSearchLoading = value => ({
  type: SET_SEARCH_LOADING,
  value
});
